// Notification.js — Apple-style banner (iOS / macOS notification look):
// frosted-glass capsule dropping from the top centre of the screen, with a
// filled round status icon. Used by every screen, and by react-hot-toast
// through <AppleToaster/> (see App.js), so all pop-ups read the same.
import React from 'react';
import {createPortal} from 'react-dom';
import {Toaster, resolveValue, toast} from 'react-hot-toast';
import {Check, X, AlertTriangle, Info, Loader} from "lucide-react";

// System colours (Apple HIG): green, red, orange, blue
const ICONS = {
    success: {Icon: Check, className: 'bg-[#34C759]'},
    error: {Icon: X, className: 'bg-[#FF3B30]'},
    warning: {Icon: AlertTriangle, className: 'bg-[#FF9500]'},
    info: {Icon: Info, className: 'bg-[#007AFF]'},
    loading: {Icon: Loader, className: 'bg-[#007AFF]', spin: true},
};

// The banner itself, without positioning
export const NotificationBanner = ({type, message, onClose, visible = true}) => {
    const {Icon, className, spin} = ICONS[type] || ICONS.info;
    return (
        <div
            role={type === 'error' ? 'alert' : 'status'}
            aria-live={type === 'error' ? 'assertive' : 'polite'}
            className={`pointer-events-auto flex items-center gap-3 w-full max-w-sm pl-3 pr-2 py-2.5 rounded-2xl
                bg-white/85 dark:bg-gray-800/85 backdrop-blur-2xl backdrop-saturate-150
                border border-white/60 dark:border-white/10
                shadow-[0_12px_40px_-8px_rgba(0,0,0,0.3),0_0_0_0.5px_rgba(0,0,0,0.08)]
                ${visible ? 'notification-drop-in' : 'notification-drop-out'}`}
        >
            <span className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-white ${className}`}>
                <Icon className={`w-4 h-4 ${spin ? 'animate-spin' : ''}`} strokeWidth={2.5}/>
            </span>
            <p className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug break-words">
                {message}
            </p>
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fermer"
                    className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-600 hover:bg-black/5 dark:hover:text-gray-200 dark:hover:bg-white/10 transition-colors"
                >
                    <X className="w-4 h-4"/>
                </button>
            )}
        </div>
    );
};

// Portal so the banner sits above modals and isn't clipped by transformed parents
const Notification = ({show, type, message, onClose}) => {
    if (!show) return null;
    return createPortal(
        <div className="fixed top-3 sm:top-4 inset-x-0 z-[1000] flex justify-center px-4 pointer-events-none">
            <NotificationBanner type={type} message={message} onClose={onClose}/>
        </div>,
        document.body
    );
};

// react-hot-toast host, rendering every toast() as the same banner
export const AppleToaster = () => (
    <Toaster position="top-center" gutter={8} containerStyle={{top: 16, zIndex: 1000}}>
        {(t) => (
            <NotificationBanner
                type={t.type === 'blank' ? 'info' : t.type}
                message={resolveValue(t.message, t)}
                visible={t.visible}
                onClose={t.type === 'loading' ? undefined : () => toast.dismiss(t.id)}
            />
        )}
    </Toaster>
);

export default Notification;
